import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { BoolIcon, MarginIcon } from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import {
    selectedROIState,
    selectedRoiIsPointTypeState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import { useRecoilState, useRecoilValue } from 'recoil';

export default function OperationsSection() {
    const selectedStructureSet = useRecoilValue(currentStructureSetState);
    const selectedRoiNumber = useRecoilValue(selectedROIState);
    const isPointRoi = useRecoilValue(selectedRoiIsPointTypeState);
    const intl = useIntl();
    const [panelState, setPanelState] = useRecoilState(operationPanelState);

    const isDisabled =
        !selectedStructureSet ||
        selectedStructureSet.roiArray.length === 0 ||
        isPointRoi;
    // Boolean and margin always need a writable structure set: even when
    // targeting an existing ROI the new contour data has to land somewhere,
    // and an approved structure set rejects creating or modifying ROIs.
    const isStructureSetApproved = !!selectedStructureSet?.isApproved;
    const isRoiDisabled = isDisabled || selectedRoiNumber === -1;

    const handleBooleanToggle = useCallback(() => {
        setPanelState((prev) =>
            prev.type === 'boolean-operation'
                ? { type: 'closed' }
                : { type: 'boolean-operation' }
        );
    }, [setPanelState]);

    const handleMarginToggle = useCallback(() => {
        setPanelState((prev) =>
            prev.type === 'margin'
                ? { type: 'closed' }
                : { type: 'margin', mode: 'symmetric' }
        );
    }, [setPanelState]);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    useEffect(() => {
        if (
            panelState.type === 'boolean-operation' &&
            (isDisabled || isStructureSetApproved)
        ) {
            setPanelState({ type: 'closed' });
        } else if (
            panelState.type === 'margin' &&
            (isRoiDisabled || isStructureSetApproved)
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [
        panelState.type,
        isDisabled,
        isRoiDisabled,
        isStructureSetApproved,
        setPanelState,
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<BoolIcon />}
                tooltip={intl.formatMessage({
                    id: 'toolbar.booleanOperation.tooltip',
                })}
                tooltipPlacement="left"
                selected={panelState.type === 'boolean-operation'}
                disabled={isDisabled || isStructureSetApproved}
                onClick={handleBooleanToggle}
            />

            <ToolbarButton
                icon={<MarginIcon />}
                tooltip={intl.formatMessage({ id: 'toolbar.margin.tooltip' })}
                tooltipPlacement="left"
                selected={panelState.type === 'margin'}
                disabled={isRoiDisabled || isStructureSetApproved}
                onClick={handleMarginToggle}
            />
        </Box>
    );
}
